import {
  Else,
  If,
  Then,
  When,
} from "react-if";
import {
  IPolicy,
  PolicyDayPeriodType,
  PolicyIntervalType,
  PolicyState,
  PolicyType,
  getOrdinalSuffix,
  langauge,
  policyWeeklyDayIntervalMap,
  statementTypes,
  validatePolicySync,
} from "shared";
import { Tooltip } from "@mui/material";
import {
  useMemo,
  useState,
  useEffect,
} from "react";
import { ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import {
  ChevronRightIcon,
  PencilSquareIcon,
  QueueListIcon,
  EnvelopeIcon,
  FunnelIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";

import { renderDayIndex, renderDescription } from "../../../../lib/helper";
import { Paragraph } from "../../../../../common/Atoms/Typography/Paragraph";
import { Badge } from "../../../../../common/Atoms/Badge";
import { usePolicies } from "../../../../hooks/usePolicies";
import { classNames } from "../../../../../common/lib/classNames";
import { useUpdatePolicyMutation } from "../../../../services/api/policyApi/policy";
import { useActionTableModels } from "../../../../hooks/useActions";
import { useCurrentOrgUsers } from "../../../../hooks/useCurrentOrgUsers";
import { useSettings } from "../../../../hooks/useSettings";
import { useEmailTemplates } from "../../../../hooks/useEmailTemplates";
import { useAccountingSystemTerm } from '../../../../hooks/useAccountingSystemTerm';

import { PolicyStateDropdown } from "./PolicyStateDropdown";

interface ExistingPolicyItemProps {
  policy: IPolicy;
}

function renderTargetDay(policy: IPolicy) {
  let str = ``;
  if (policy.policy_type === PolicyType.DISCOUNT) {
    str = langauge.policy.targetStatus[policy.discount_removal_target_status] || `due date`;
  }
  else {
    str = langauge.policy.targetStatus[policy.target_status] || `due date`;
  }

  // Return with leading space
  return ` ${str}`;
}

export function ExistingPolicyItem({ policy }: ExistingPolicyItemProps) {
  const { data: policies } = usePolicies();
  const [updatePolicy, { isLoading: updatePolicyLoading }] = useUpdatePolicyMutation();
  const navigate = useNavigate();
  const currentOrgUsers = useCurrentOrgUsers();
  const { data: settings } = useSettings();
  const { setFilterModel, setCurrentTab } = useActionTableModels();
  const { data: emailTemplates } = useEmailTemplates();

  // Track optimistic state for immediate UI feedback
  const [optimisticState, setOptimisticState] = useState<{
    new?: PolicyState;
    current: PolicyState;
  }>({
    current: policy.state,
  });

  // Sync optimistic state with actual policy state when it changes
  useEffect(() => {
    setOptimisticState({
      current: policy.state,
      new: undefined,
    });
  }, [policy.state]);

  const senderProfiles = settings?.senderProfiles || [];
  const contactGroupsTerm = useAccountingSystemTerm(`Contact Groups`);
  const policyDescription = renderDescription(policy, currentOrgUsers, senderProfiles, contactGroupsTerm);
  const descriptionParts = policyDescription.description.split(` | `).filter(p => p.trim());

  // Find the email template if one is set
  const emailTemplate = useMemo(() => {
    if (!policy.template_id || !emailTemplates) return null;
    const template = emailTemplates.find(t => t.id === policy.template_id);

    return template || null;
  }, [policy.template_id, emailTemplates]);

  // Check if this policy type sends emails
  const policyUsesEmail = useMemo(() => {
    return [
      PolicyType.REMINDER,
      PolicyType.STATEMENT,
      PolicyType.LATE_FEE,
      PolicyType.STATEMENT_LATE_FEE,
      PolicyType.DISCOUNT,
      PolicyType.QUOTE_EMAIL,
    ].includes(policy.policy_type);
  }, [policy.policy_type]);

  function onClick() {
    navigate(`policies/${policy.id}`);
  }

  const policyValid = useMemo(() => {
    let policyValidVar = true;

    try {
      validatePolicySync(policy);
    }
    catch (e) {
      console.warn(e);
      policyValidVar = false;
    }

    return policyValidVar;
  }, [policy]);

  const groupIds = useMemo(() => {
    return policies?.find(p => p.policy.id === policy.id)?.groupIds || [];
  }, [ policies, policy]);

  async function onStateChange(e: React.MouseEvent<HTMLButtonElement, MouseEvent>, newState: string) {
    e.preventDefault();
    e.stopPropagation();

    toast.loading(`Updating policy state...`, { toastId: `update-policy-state-${policy.id}` });

    const policyFromCache = policies?.find(p => p.policy.id === policy.id);

    if (!policyFromCache) return;

    // Update optimistic state immediately for instant UI feedback
    setOptimisticState({
      new: newState as PolicyState,
      current: policyFromCache.policy.state,
    });

    try{
      await updatePolicy({
        ...policyFromCache,
        policy: {
          ...policyFromCache.policy,
          state: newState as PolicyState,
        },
        organisationId: policy.organisation_id,
        id: policy.id,
      }).unwrap();

      toast.update(`update-policy-state-${policy.id}`, {
        render: `Policy state updated successfully.`,
        type: `success`,
        isLoading: false,
        autoClose: 3000,
      });
    }
    catch (error) {
      // Revert optimistic state on error
      setOptimisticState({
        current: policyFromCache.policy.state,
        new: undefined,
      });

      toast.update(`update-policy-state-${policy.id}`, {
        render: `Failed to update policy state.`,
        type: `error`,
        isLoading: false,
        autoClose: 5000,
      });

      console.error(`Failed to update policy state:`, error);
    }
  }

  function onViewActions(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    e.preventDefault();
    e.stopPropagation();
    setCurrentTab(null);

    setFilterModel({
      items: [
        {
          field: `policyId`,
          operator: `is`,
          value: policy.id,
          id: Math.round(Math.random() * 1e5),
        },
      ],
    });

    navigate(`/${policy.organisation_id}/actions?persist=true`);
  }

  return (
    <div key={ policy.id }
      className={ classNames(
        `text-left border border-gray-100 p-2 rounded-md shadow-md w-full cursor-pointer bg-white`,
        policyValid ? `` : `opacity-75 border-red-500 border-2`,
      ) }
      onClick={ onClick }
    >
      <div className={ `flex gap-3` }>
        { /* Column 1 - Day index etc */ }
        <div className={
          `w-24 shrink-0 self-center bg-gray-50 rounded-md p-2 border border-gray-200 text-center`
        }>
          <If condition={ !statementTypes.includes(policy.policy_type) }>
            <Then>
              { /*  Is Invoice or ContactEsc Type */ }
              {
                () => (
                  <>
                    <If condition={ renderDayIndex(policy).day !== `0` }>
                      <Then>
                        <p className={ `text-sm font-semibold text-gray-900` }>
                          { renderDayIndex(policy).day }
                          { ` ` }
                          {
                            (policy.day_period_type === PolicyDayPeriodType.WEEKDAYS || policy.day_period_type === PolicyDayPeriodType.WEEKDAYS_OR_LATER) ? `weekday` : `day`
                          }
                          {
                            renderDayIndex(policy).day === `1` ? `` : `s`
                          }
                        </p>
                        <p className={ `text-xs text-gray-600 mt-1 leading-snug` }>
                          { renderDayIndex(policy).verb }{ renderTargetDay(policy) }
                          <When condition={ policy.day_period_type === PolicyDayPeriodType.DAYS_OR_LATER || policy.day_period_type === PolicyDayPeriodType.WEEKDAYS_OR_LATER }>
                            { ` (or later)` }
                          </When>
                        </p>
                      </Then>
                      <Else>
                        <p className={ `text-sm font-semibold text-gray-900` }>
                          { `On` }
                        </p>
                        <p className={ `text-xs text-gray-600 mt-1 leading-snug` }>
                          { renderTargetDay(policy).trim() }
                        </p>
                      </Else>
                    </If>
                  </>
                )
              }
            </Then>
            <Else>
              { /* Is statement/interval based */ }
              {
                () => (
                  policy.interval_type === PolicyIntervalType.MONTHLY || policy.interval_type === PolicyIntervalType.MONTHLY_WEEKDAYS ? (
                    <p className={ `text-sm font-semibold text-gray-900` }>
                      { `${policy.day_index}${getOrdinalSuffix(policy.day_index || 1)} of month` }
                    </p>
                  ) : (
                    <p className={ `text-sm font-semibold text-gray-900` }>
                      { `${policyWeeklyDayIntervalMap[policy.day_index]} weekly` }
                    </p>
                  )
                )
              }
            </Else>
          </If>
        </div>

        { /* Column 2 Badge, descriptions and edit icon */ }
        <div className={ `flex-1 min-w-0` }>
          { /* Badges */ }
          <div className={ `flex gap-2 mb-1` }>
            <When condition={ groupIds?.length > 1 }>
              <Tooltip title={ `This policy is assigned to multiple groups. Changes will affect all groups.` }>
                <Badge color={ `orange` }
                  className={ `max-w-fit` }>
                  { `Multiple Groups` }
                </Badge>
              </Tooltip>
            </When>

            <When condition={ policyValid && policy.only_webhook }>
              <Tooltip title={ `Will not send directly to contacts, will only trigger webhooks` }>
                <Badge color={ `orange` }
                  className={ `max-w-fit` }>
                  { `Webhook Only` }
                </Badge>
              </Tooltip>
            </When>
          </div>

          <Paragraph className={ `mb-1 text-sm font-medium flex items-center gap-1` }>
            { policy.title }
            <When condition={ !policyValid }>
              <Tooltip title={ `Policy has incomplete settings. Click to edit policy` }>
                <ExclamationTriangleIcon
                  color={ `red` }
                  className={ `h-4 w-4 text-red-450` }
                />
              </Tooltip>
            </When>
          </Paragraph>

          <Paragraph
            variant={ `secondary` }
            className={ `text-xs leading-relaxed` }
          >
            { descriptionParts.map((desc, i) => {
              return (
                <span key={ i }>
                  <ChevronRightIcon className={ `w-3 h-3 inline-block` }/>{ desc }
                  { i < descriptionParts.length - 1 && ` ` }
                </span>
              );
            }) }
          </Paragraph>
          <When condition={ !!policyDescription.secondaryDescription }>
            <div className={ `flex items-center gap-1.5 text-xs text-gray-500 mt-1` }>
              <FunnelIcon className={ `w-4 h-4` } />
              <span className={ `text-gray-700` }>{ policyDescription.secondaryDescription }</span>
            </div>
          </When>
          <When condition={ policyUsesEmail }>
            <div className={ `flex items-center gap-1.5 text-xs text-gray-500 mt-1` }>
              <EnvelopeIcon className={ `w-4 h-4` } />
              <If condition={ !!emailTemplate }>
                <Then>
                  <Tooltip
                    title={
                      <div className={ `p-2` }>
                        <div className={ `font-semibold mb-1` }>{ emailTemplate?.name }</div>
                        <div className={ `text-xs` }>
                          <strong>{ `Subject:` }</strong> { emailTemplate?.subject || `No subject` }
                        </div>
                      </div>
                    }
                    placement={ `top` }
                  >
                    <span className={ `text-gray-700 cursor-help` }>{ emailTemplate?.name }</span>
                  </Tooltip>
                </Then>
                <Else>
                  <span className={ `italic` }>{ `No email` }</span>
                </Else>
              </If>
            </div>
          </When>
        </div>

        { /* Column 3 - Policy State and Actions */ }
        <div className={ `shrink-0 flex flex-col items-end justify-center gap-2 xl:flex-row xl:items-center xl:gap-3` }>
          <div className={ `flex flex-col gap-2 xl:flex-row` }>
            <button
              onClick={ e => {
                e.stopPropagation();
                onClick();
              } }
              className={ `flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 transition-colors` }
            >
              <PencilSquareIcon className={ `h-4 w-4` } />
              { `Edit` }
            </button>

            <Tooltip title={ `View all related actions for this policy` }>
              <button
                onClick={ onViewActions }
                className={ `flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 transition-colors` }
              >
                <QueueListIcon className={ `h-4 w-4` } />
                { `Actions` }
              </button>
            </Tooltip>
          </div>

          <PolicyStateDropdown
            value={ optimisticState.new || optimisticState.current }
            onChange={ onStateChange }
            loading={ updatePolicyLoading }
            disabled={ !policyValid }
          />
        </div>
      </div>
    </div>
  );
}
